import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import IconAction from "@/components/IconAction";
import { Eye, FileText, ExternalLink, Mail, CheckCircle2, RotateCcw, Ban, AlertCircle, X, Clock, Inbox, Pause, Play, Zap, Send, Loader2, Target, RefreshCw, Trash2, Paperclip, Bell, Download, Archive, Pencil, CheckSquare, Square, History as HistoryIcon } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import ManualReminderDialog from "@/pages/invoices/ManualReminderDialog";
import RemindersPausedBadge from "@/components/RemindersPausedBadge";
import AutoInvoiceForecastWidget from "@/components/AutoInvoiceForecastWidget";
import UpcomingReceivablesWidget from "@/components/UpcomingReceivablesWidget";
import PaymentDelayChip from "@/components/PaymentDelayChip";
import RetroInvoiceBlock from "@/components/RetroInvoiceDialog";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

function StatusBadge({ inv }) {
  // Iter-194j — overdue badge has priority over "sent" so the CEO sees the
  // dunning state at a glance. Cancellation/paid still trump it.
  if (inv.cancelled) {
    return <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 bg-zinc-200 text-zinc-700 rounded font-mono uppercase line-through" data-testid={`status-cancelled-${inv.invoice_id}`} title={inv.cancellation_reason || "Cancelled"}>Cancelled</span>;
  }
  if (inv.paid_at) {
    // Iter-200c — differentiate payment source: client self-declaration vs
    // manual CEO mark vs CEO-validated declaration.
    const via = inv.paid_via || "manual";
    const isClient = via === "client_self_declared";
    const isValidated = via === "ceo_validated_declaration";
    const sourceIcon = isClient ? "🌐" : (isValidated ? "✓🌐" : "✏️");
    const sourceLabel = isClient ? "Auto-déclarée par le client" : (isValidated ? "Déclaration client validée par BAIM" : "Marquée manuellement par BAIM");
    return (
      <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded font-mono uppercase"
        data-testid={`status-paid-${inv.invoice_id}`} title={sourceLabel}>
        Paid <span className="ml-0.5" data-testid={`paid-source-${inv.invoice_id}`}>{sourceIcon}</span>
      </span>
    );
  }
  if (inv.payment_overdue) {
    return <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 bg-red-100 text-red-800 rounded font-mono uppercase" data-testid={`status-overdue-${inv.invoice_id}`} title="Délai de règlement par client dépassé">⚠ Overdue</span>;
  }
  if (inv.sent_at) {
    return <SentBadgeWithHistory inv={inv} />;
  }
  return <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded font-mono uppercase" data-testid={`status-draft-${inv.invoice_id}`}>À send</span>;
}

// Iter-194j — Inline date strip showing send + due dates, with day-counter
// and an "overdue" pill once the deadline has passed.
function InvoiceDateStrip({ inv, onReload }) {
  if (!inv.sent_at) {
    return <div className="text-[10px] font-mono text-zinc-400">Pas encore envoyée</div>;
  }
  const fmt = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "2-digit" }); }
    catch (_e) { return iso?.slice(0, 10) || "—"; }
  };
  const dayDiff = (() => {
    try {
      const s = new Date(inv.sent_at);
      const today = new Date();
      return Math.floor((today - s) / 86400000);
    } catch (_e) { return null; }
  })();
  const overdue = !!inv.payment_overdue;
  const remindersCount = (inv.payment_reminders_log || []).length || (inv.payment_reminders_sent || []).length;
  return (
    <div className={`text-[10px] font-mono flex flex-col gap-1 ${overdue ? "text-red-700" : "text-zinc-500"}`} data-testid={`invoices-dates-${inv.invoice_id}`}>
      <div className="inline-flex items-center gap-1">
        <Send size={10} className="shrink-0" />
        <span>{fmt(inv.sent_at)}</span>
        {dayDiff !== null && <span className="opacity-70">· J+{dayDiff}</span>}
      </div>
      {inv.due_date && (
        <div className="inline-flex items-center gap-1">
          <Target size={10} className="shrink-0" />
          <span>{fmt(inv.due_date)}</span>
          {inv.billing_delay_months ? <span className="opacity-70">· délai {inv.billing_delay_months} mois</span> : null}
        </div>
      )}
      {/* Iter-196d — effective payment-delay chip. Clickable for the CEO to
          override (e.g. exceptional 60-day deadline granted to one client). */}
      <PaymentDelayChip inv={inv} onChanged={onReload} />
      {remindersCount > 0 && <InvoiceRemindersLogPopover inv={inv} count={remindersCount} />}
    </div>
  );
}


// Iter-194u — clickable mini-badge showing the payment_reminders_log audit
// trail (milestone + overdue emails). Hidden when no reminder has been sent.
function InvoiceRemindersLogPopover({ inv, count }) {
  const [log, setLog] = useState(inv.payment_reminders_log || null);
  const [loading, setLoading] = useState(false);
  const fetchLog = async () => {
    if (log) return;
    setLoading(true);
    try {
      const r = await api.get(`/invoices/${inv.invoice_id}/reminders-log`);
      setLog(r.data?.payment_reminders_log || []);
    } catch (_e) {
      setLog([]);
    } finally { setLoading(false); }
  };
  const fmtTs = (iso) => {
    try { return new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
    catch (_e) { return iso || "—"; }
  };
  return (
    <Popover>
      <PopoverTrigger asChild>
        <span
          onClick={fetchLog}
          className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded cursor-pointer hover:bg-amber-200 transition-colors mt-0.5 w-fit"
          data-testid={`invoices-reminders-${inv.invoice_id}`}
          role="button"
          tabIndex={0}
        >
          <Mail size={10} className="shrink-0" />
          <span>{count} relance{count > 1 ? "s" : ""}</span>
        </span>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[420px] text-xs p-0 border-zinc-200 max-h-[420px] overflow-y-auto"
        data-testid={`reminders-log-popover-${inv.invoice_id}`}
      >
        <div className="p-3 border-b border-zinc-100 bg-amber-50">
          <div className="font-semibold text-amber-900">Historique des relances</div>
          <div className="text-[10px] text-amber-700">Facture {inv.number || inv.invoice_id}</div>
        </div>
        {loading && <div className="p-4 text-center text-zinc-500">Chargement…</div>}
        {!loading && (!log || log.length === 0) && (
          <div className="p-4 text-center text-zinc-400 italic">Aucune relance enregistrée.</div>
        )}
        {!loading && log && log.length > 0 && (
          <div className="divide-y divide-zinc-100">
            {log.map((r, i) => (
              <div key={i} className="p-3" data-testid={`reminders-log-row-${inv.invoice_id}-${i}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className={`text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded ${
                    r.kind === "overdue" ? "bg-red-100 text-red-800" : "bg-amber-100 text-amber-800"
                  }`}>
                    {r.kind === "overdue" ? "Dépassement" : r.tier || `J+${r.milestone}`}
                  </span>
                  <span className="text-[10px] text-zinc-500">{fmtTs(r.sent_at)}</span>
                </div>
                <div className="text-[11px] text-zinc-700 font-mono truncate" title={r.subject}>{r.subject}</div>
                <div className="text-[10px] text-zinc-500 font-mono truncate">→ {r.to}{r.cc && r.cc.length ? ` (+ ${r.cc.length} cc)` : ""}</div>
              </div>
            ))}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

function SentBadgeWithHistory({ inv }) {
  const fmtDateTime = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleString("fr-FR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch (_e) { return iso || "—"; }
  };
  const gmailUrl = inv.email_message_id
    ? `https://mail.google.com/mail/u/0/#search/rfc822msgid%3A${encodeURIComponent(inv.email_message_id)}`
    : null;
  const attachments = Array.isArray(inv.last_email_attachments) ? inv.last_email_attachments : [];
  // Iter-206v — clickable attachments. We map a filename back to its serving
  // endpoint: if it matches the persisted `inv.pdf_filename` → invoice PDF;
  // otherwise treat as the Monthly CRA. Fetched via blob URL so the JWT auth
  // header rides on the request (raw <a href> would 401 → SPA home).
  const [openingFile, setOpeningFile] = useState(null);
  const openAttachment = async (fname) => {
    const isInvoicePdf = inv.pdf_filename && fname === inv.pdf_filename;
    const endpoint = isInvoicePdf
      ? `/invoices/${inv.invoice_id}/pdf`
      : `/invoices/${inv.invoice_id}/cra-pdf`;
    setOpeningFile(fname);
    try {
      const r = await api.get(endpoint, {
        responseType: "blob",
        params: { v: (isInvoicePdf ? inv.email_message_id : inv.cra_pdf_built_at) || Date.now() },
      });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => window.URL.revokeObjectURL(url), 30000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Impossible d'ouvrir ${fname}`);
    } finally {
      setOpeningFile(null);
    }
  };
  return (
    <div className="inline-flex items-center gap-1">
      <Popover>
        <PopoverTrigger asChild>
          <span
            data-testid={`status-sent-${inv.invoice_id}`}
            className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded font-mono uppercase cursor-default hover:bg-blue-200 transition-colors"
            tabIndex={0}
            role="button"
            aria-label="Show send history"
          >
            <Mail size={10} /> Sent
          </span>
        </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[360px] text-xs p-0 border-zinc-200"
        data-testid={`send-history-${inv.invoice_id}`}
      >
        <div className="p-3 border-b border-zinc-100 bg-blue-50">
          <div className="flex items-center gap-1.5 text-blue-900 font-semibold">
            <CheckCircle2 size={13} className="text-blue-600" />
            <span>Email sent · {fmtDateTime(inv.sent_at)}</span>
          </div>
          {inv.email_body_overridden && (
            <div className="text-[10px] text-blue-700 mt-1">Body was edited before sending</div>
          )}
        </div>
        <div className="p-3 space-y-1.5 font-mono text-[11px]">
          <div className="flex gap-2">
            <span className="text-zinc-500 w-12 shrink-0">From</span>
            <span className="text-zinc-800 break-all">{inv.email_from || "—"}</span>
          </div>
          <div className="flex gap-2">
            <span className="text-zinc-500 w-12 shrink-0">To</span>
            <span className="text-zinc-800 break-all">{inv.email_to || "—"}</span>
          </div>
          <div className="flex gap-2">
            <span className="text-zinc-500 w-12 shrink-0">Cc</span>
            <span className="text-zinc-800 break-all">{(inv.email_cc || []).join(", ") || "—"}</span>
          </div>
          {inv.email_message_id && (
            <div className="flex gap-2">
              <span className="text-zinc-500 w-12 shrink-0">Msg ID</span>
              <span className="text-zinc-700 break-all text-[10px]">{inv.email_message_id}</span>
            </div>
          )}
          {/* Iter-206u/v — clickable list of attachments persisted at send time */}
          {attachments.length > 0 && (
            <div className="flex gap-2 pt-1.5 border-t border-zinc-100 mt-1.5" data-testid={`sent-attachments-${inv.invoice_id}`}>
              <span className="text-zinc-500 w-12 shrink-0">
                <span className="inline-flex items-center gap-1">
                  <Paperclip size={10} /> {attachments.length}
                </span>
              </span>
              <div className="flex-1 space-y-0.5">
                {attachments.map((fname, idx) => {
                  const isInvoicePdf = inv.pdf_filename && fname === inv.pdf_filename;
                  return (
                    <button
                      key={`${fname}-${idx}`}
                      type="button"
                      onClick={() => openAttachment(fname)}
                      disabled={openingFile === fname}
                      data-testid={`sent-attachment-${inv.invoice_id}-${idx}`}
                      title={`Ouvrir ${fname} dans un nouvel onglet`}
                      className="flex items-center gap-1 text-blue-700 hover:text-blue-900 hover:underline break-all leading-tight text-left w-full disabled:opacity-50"
                    >
                      {openingFile === fname
                        ? <Loader2 size={9} className="animate-spin shrink-0" />
                        : (isInvoicePdf ? <FileText size={9} className="shrink-0" /> : <Eye size={9} className="shrink-0" />)
                      }
                      <span className="break-all">{fname}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
        <div className="p-3 border-t border-zinc-100 bg-zinc-50">
          <div className="flex items-center gap-1.5 text-[11px] text-zinc-700">
            <Clock size={11} className="text-amber-600" />
            <span><strong>Read receipt</strong> requested · status not auto-tracked</span>
          </div>
          <div className="text-[10px] text-zinc-500 mt-1 leading-relaxed">
            The recipient's mail client (Gmail/Outlook) will prompt them to send a delivery notification — it lands as a regular email in your inbox.
          </div>
        </div>
        {gmailUrl && (
          <a
            href={gmailUrl}
            target="_blank"
            rel="noreferrer"
            data-testid={`send-history-gmail-link-${inv.invoice_id}`}
            className="block px-3 py-2 border-t border-zinc-100 text-blue-700 hover:bg-blue-50 text-[11px] font-mono inline-flex items-center gap-1.5"
          >
            <Inbox size={11} /> Open this thread in Gmail
            <ExternalLink size={10} />
          </a>
        )}
      </PopoverContent>
    </Popover>
      {attachments.length > 0 && (
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              data-testid={`attachments-badge-${inv.invoice_id}`}
              aria-label={`${attachments.length} pièces jointes — cliquer pour ouvrir`}
              className="inline-flex items-center gap-0.5 text-[10px] px-1 py-0.5 bg-zinc-100 text-zinc-700 rounded font-mono cursor-pointer border border-zinc-200 hover:bg-zinc-200 transition-colors"
            >
              <Paperclip size={9} /> {attachments.length}
            </button>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            className="w-[320px] text-xs p-0 border-zinc-200"
            data-testid={`attachments-popover-${inv.invoice_id}`}
          >
            <div className="p-3 border-b border-zinc-100 bg-zinc-50 font-mono text-[11px] uppercase tracking-wider text-zinc-600 flex items-center gap-1.5">
              <Paperclip size={11} /> {attachments.length} pièce(s) jointe(s)
            </div>
            <div className="p-3 space-y-1.5">
              {attachments.map((fname, idx) => {
                const isInvoicePdf = inv.pdf_filename && fname === inv.pdf_filename;
                return (
                  <button
                    key={`pop-${fname}-${idx}`}
                    type="button"
                    onClick={() => openAttachment(fname)}
                    disabled={openingFile === fname}
                    data-testid={`attachments-popover-item-${inv.invoice_id}-${idx}`}
                    title={`Ouvrir ${fname} (cliquer pour télécharger via API authentifiée)`}
                    className="w-full flex items-start gap-2 px-2 py-1.5 rounded hover:bg-blue-50 text-left disabled:opacity-50 group"
                  >
                    {openingFile === fname
                      ? <Loader2 size={12} className="animate-spin shrink-0 mt-0.5 text-blue-600" />
                      : (isInvoicePdf
                          ? <FileText size={12} className="shrink-0 mt-0.5 text-blue-700 group-hover:text-blue-900" />
                          : <Eye size={12} className="shrink-0 mt-0.5 text-emerald-700 group-hover:text-emerald-900" />)
                    }
                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-0.5">
                        {isInvoicePdf ? "Facture PDF" : "Monthly CRA"}
                      </div>
                      <div className="text-[11px] text-zinc-800 break-all leading-tight group-hover:text-blue-900">
                        {fname}
                      </div>
                    </div>
                    <ExternalLink size={10} className="shrink-0 mt-0.5 text-zinc-400 group-hover:text-blue-600" />
                  </button>
                );
              })}
            </div>
            <div className="px-3 py-2 border-t border-zinc-100 bg-zinc-50 text-[10px] text-zinc-500 leading-relaxed">
              Cliquez sur un fichier pour l'ouvrir dans un nouvel onglet — c'est exactement la pièce jointe que le client a reçue.
            </div>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}

// Iter-194w — Bulk reminder button for the CEO/Gestionnaire weekly relance
// session. Iterates all approaching + overdue invoices and fires the
// auto-picked email per invoice. Confirmation prompt shows the count first.
function BulkReminderButton({ onDone }) {
  const [counts, setCounts] = useState(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const fetchCounts = async () => {
    try {
      const r = await api.get("/admin/invoices/payment-status-counts");
      setCounts(r.data || {});
      setOpen(true);
    } catch (_e) { /* silent */ }
  };
  const run = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/invoices/bulk-send-reminder", { scope: "both" });
      const d = r.data || {};
      if ((d.sent || 0) > 0 && (d.errors || 0) === 0) {
        toast.success(`Relance groupée — ${d.sent} envoyée(s), ${d.skipped} skip, 0 erreur`);
      } else if ((d.errors || 0) > 0 && (d.sent || 0) === 0) {
        toast.error(`Aucun envoi — ${d.errors} erreur(s) (${d.skipped} skip)`);
      } else {
        toast.message(`Partiel — ${d.sent} ok · ${d.skipped} skip · ${d.errors} erreur(s)`);
      }
      setOpen(false);
      onDone && onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec");
    } finally { setBusy(false); }
  };
  const target = (counts?.approaching || 0) + (counts?.overdue || 0);
  return (
    <>
      <button
        onClick={fetchCounts}
        data-testid="invoices-bulk-remind-btn"
        className="ml-2 inline-flex items-center gap-1.5 px-3 h-7 bg-amber-50 border border-amber-300 text-amber-900 text-[11px] uppercase tracking-wider rounded hover:bg-amber-100"
        title="Envoyer une relance à toutes les factures à relancer"
      >
        <Send size={11} /> Tout relancer
      </button>
      <Dialog open={open} onOpenChange={(o) => !o && !busy && setOpen(false)}>
        <DialogContent className="max-w-md" data-testid="bulk-remind-dialog">
          <DialogHeader>
            <DialogTitle>Relance groupée</DialogTitle>
            <DialogDescription>
              Cette action enverra automatiquement la relance adéquate (milestone ou
              dépassement) à chaque facture concernée.
            </DialogDescription>
          </DialogHeader>
          {counts && (
            <div className="space-y-2 text-sm">
              <div className="flex justify-between bg-amber-50 border border-amber-200 rounded px-3 py-2">
                <span>Délai approche (relances en cours)</span>
                <b data-testid="bulk-remind-approaching">{counts.approaching || 0}</b>
              </div>
              <div className="flex justify-between bg-red-50 border border-red-200 rounded px-3 py-2">
                <span>En retard (délai dépassé)</span>
                <b data-testid="bulk-remind-overdue">{counts.overdue || 0}</b>
              </div>
              <div className="flex justify-between border-t border-zinc-100 pt-2 text-zinc-700 font-mono text-xs">
                <span>Total emails à envoyer</span>
                <b data-testid="bulk-remind-total">{target}</b>
              </div>
            </div>
          )}
          <DialogFooter>
            <button
              onClick={() => setOpen(false)}
              disabled={busy}
              className="px-3 h-9 text-xs border border-zinc-200 rounded hover:bg-zinc-50"
            >
              Annuler
            </button>
            <button
              onClick={run}
              disabled={busy || target === 0}
              data-testid="bulk-remind-confirm"
              className="inline-flex items-center gap-2 px-4 h-9 bg-zinc-950 text-white text-xs rounded hover:bg-zinc-800 disabled:opacity-50"
            >
              {busy ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
              {target === 0 ? "Aucune relance à envoyer" : `Envoyer ${target} relance(s)`}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function Invoices() {
  const { user: currentUser } = useAuth();
  const canCancel = currentUser?.role === "CEO" || currentUser?.role === "ADMIN";
  const canPauseFreelance = currentUser?.role === "CEO" || currentUser?.role === "GESTIONNAIRE";

  const togglePauseUnlock = async (inv) => {
    const action = inv.freelance_unlock_status === "PAUSED" ? "unpause" : "pause";
    try {
      await api.post(`/invoices/${inv.invoice_id}/freelance-unlock/${action}`);
      toast.success(action === "pause" ? "Compteur freelance mis en pause" : "Compteur freelance repris");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Échec ${action}`);
    }
  };

  const forceUnlockNow = async (inv) => {
    if (!window.confirm("Court-circuiter le délai de grâce restant et débloquer immédiatement les freelances ?")) return;
    try {
      await api.post(`/invoices/${inv.invoice_id}/freelance-unlock/force-now`);
      toast.success("Freelances débloqués — emails de soumission envoyés");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec déverrouillage");
    }
  };

  const previewCra = async (inv) => {
    try {
      const r = await api.get(`/cra-monthly/${inv.mission_id}/${inv.year}/${inv.month}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "PDF CRA indisponible");
    }
  };
  const [items, setItems] = useState([]);
  const [missionsById, setMissionsById] = useState({});
  const [viewInv, setViewInv] = useState(null);
  const [payTarget, setPayTarget] = useState(null);
  const [retroEdit, setRetroEdit] = useState(null); // Iter-259 — edit retro invoice
  const [retroHistoryTarget, setRetroHistoryTarget] = useState(null); // Iter-261 — edit change log
  // Iter-240 — post-payment MAD counter-value edit (foreign currency invoices).
  const [madEditTarget, setMadEditTarget] = useState(null);
  const [madEditVal, setMadEditVal] = useState("");
  const [madSaving, setMadSaving] = useState(false);
  // Iter-240 — exports + ZIP selection mode.
  const [exporting, setExporting] = useState(null);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [payProofFile, setPayProofFile] = useState(null);
  const [markingPaid, setMarkingPaid] = useState(false);
  // Iter-194v — manual reminder dialog target invoice (null = closed)
  const [manualReminderInvoice, setManualReminderInvoice] = useState(null);
  const [payForm, setPayForm] = useState({ paid_date: "", paid_amount: "" });
  const [cancelTarget, setCancelTarget] = useState(null);
  // Iter-206g — bumped each time we regenerate the PDF; appended as `?v=`
  // to the iframe src so the browser fetches a fresh copy.
  const [pdfCacheBuster, setPdfCacheBuster] = useState(0);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelling, setCancelling] = useState(false);
  const [sendTarget, setSendTarget] = useState(null);
  const [sendError, setSendError] = useState(null);
  const [sending, setSending] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [emailPreview, setEmailPreview] = useState(null);
  const [editedBody, setEditedBody] = useState("");
  const [editedSubject, setEditedSubject] = useState("");
  // Iter-52: inline form to fill the missing mission contact straight from the Send dialog.
  // On submit, PATCH /missions/{id} with both fields, which auto-propagates to the parent client.
  const [inlineContact, setInlineContact] = useState({ name: "", email: "", gender: "" });
  const [savingContact, setSavingContact] = useState(false);
  // Iter-193s — CEO-only ad-hoc recipient overrides (different To, arbitrary CC).
  const isCeo = currentUser?.role === "CEO";
  const [editedToEmail, setEditedToEmail] = useState("");
  const [editedCcList, setEditedCcList] = useState([]);
  // Iter-194j — multi-status filter (toggle multiple chips). Empty Set = show all.
  // Iter-194u — auto-apply filter from URL `?payment_status=overdue|approaching|active|paid`
  // Iter-228w-bis — set of invoice IDs whose snapshotted bank account drifted
  // from the currently resolved one. Loaded once on mount + after a regen.
  const [driftedIds, setDriftedIds] = useState(new Set());
  const loadDrifted = async () => {
    try {
      const r = await api.get("/invoices/_bank-drift-list");
      setDriftedIds(new Set(r.data?.invoice_ids || []));
    } catch { /* swallow — best-effort UI hint */ }
  };
  useEffect(() => { loadDrifted(); }, []);

  // Iter-228w-bis — regenerate one drifted invoice via the existing endpoint.
  const regenerateBankDrifted = async (inv) => {
    if (!window.confirm(
      `Régénérer le PDF de la facture ${inv.number} avec les nouvelles coordonnées bancaires ?`
    )) return;
    try {
      await api.post(`/invoices/${inv.invoice_id}/regenerate-pdf`);
      toast.success(`PDF de ${inv.number} régénéré`);
      loadDrifted();
      load && load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec");
    }
  };

  // Iter-230 — single-select filter. Default view = "En cours" (à envoyer +
  // envoyée + délai dépassé). Cancelled invoices are ARCHIVED: they only ever
  // appear under the dedicated "Annulée" filter (excluded even from "Toutes").
  const [statusFilter, setStatusFilter] = useState(() => {
    if (typeof window === "undefined") return "ongoing";
    const p = new URLSearchParams(window.location.search).get("payment_status");
    if (p === "overdue") return "overdue";
    if (p === "approaching" || p === "active") return "sent";
    if (p === "paid") return "paid";
    if (p === "cancelled") return "cancelled";
    return "ongoing";
  });
  const invoiceStatusKey = (inv) => {
    if (inv.cancelled) return "cancelled";
    if (inv.paid_at) return "paid";
    if (inv.payment_overdue) return "overdue";
    if (inv.sent_at) return "sent";
    return "draft";
  };
  const matchesStatusFilter = (inv) => {
    const s = invoiceStatusKey(inv);
    if (statusFilter === "ongoing") return s === "draft" || s === "sent" || s === "overdue";
    if (statusFilter === "all") return s !== "cancelled";
    return s === statusFilter;
  };

  // Iter-241 — advanced filters (client / currency / sent-date range) applied
  // on top of the status filter; exports & ZIP selection follow them too.
  const [clientFilter, setClientFilter] = useState("");
  const [currencyFilter, setCurrencyFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const matchesAdvancedFilters = (inv) => {
    if (clientFilter && (inv.client_name || "—") !== clientFilter) return false;
    if (currencyFilter && (inv.currency || "MAD").toUpperCase() !== currencyFilter) return false;
    if (dateFrom || dateTo) {
      const d = (inv.sent_at || "").slice(0, 10);
      if (!d) return false;
      if (dateFrom && d < dateFrom) return false;
      if (dateTo && d > dateTo) return false;
    }
    return true;
  };
  const matchesAllFilters = (inv) => matchesStatusFilter(inv) && matchesAdvancedFilters(inv);
  const advancedActive = !!(clientFilter || currencyFilter || dateFrom || dateTo);
  const resetAdvancedFilters = () => { setClientFilter(""); setCurrencyFilter(""); setDateFrom(""); setDateTo(""); };

  const load = async () => {
    try {
      const [invs, miss] = await Promise.all([api.get("/invoices"), api.get("/missions")]);
      setItems(invs.data);
      const map = {};
      (miss.data || []).forEach((m) => { map[m.mission_id] = m; });
      setMissionsById(map);
    } catch (_e) { /* swallow */ }
  };
  useEffect(() => { load(); }, []);
  const fmt = (n) => (n ?? 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const openSend = async (inv, mode = "send") => {
    setSendError(null);
    setEmailPreview(null);
    setEditedBody("");
    setEditedSubject("");
    const mission = missionsById[inv.mission_id];
    setSendTarget({ inv, mission, mode });
    // Pre-populate inline form with whatever the mission already has (likely empty when missing)
    setInlineContact({
      name: mission?.contact_name || "",
      email: mission?.contact_email || "",
      gender: mission?.contact_gender || "",
    });
    setPreviewLoading(true);
    try {
      const r = await api.get(`/invoices/${inv.invoice_id}/send-email/preview`);
      setEmailPreview(r.data);
      setEditedBody(r.data.body_text || "");
      setEditedSubject(r.data.subject || "");
      setEditedToEmail(r.data.to_email || "");
      setEditedCcList(Array.isArray(r.data.cc_emails) ? [...r.data.cc_emails] : []);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code) {
        setSendError(detail);
      } else if (typeof detail === "string") {
        setSendError({ code: "preview_failed", message: detail });
      } else {
        setSendError({ code: "preview_failed", message: "Could not load the email preview." });
      }
    } finally { setPreviewLoading(false); }
  };

  const saveInlineContact = async () => {
    if (!sendTarget?.mission) return;
    const name = inlineContact.name.trim();
    const email = inlineContact.email.trim();
    const gender = (inlineContact.gender || "").trim();
    if (!gender) {
      toast.error("Please pick a civility (M. / Mme. / M./Mme)");
      return;
    }
    if (!email || (!name && gender !== "M./Mme")) {
      toast.error("Please fill the contact email" + (gender !== "M./Mme" ? " and contact name" : ""));
      return;
    }
    setSavingContact(true);
    try {
      // PATCH the mission — backend auto-propagates to db.clients.
      await api.patch(`/missions/${sendTarget.mission.mission_id}`, {
        contact_name: gender === "M./Mme" ? "" : name,
        contact_email: email,
        contact_gender: gender,
      });
      toast.success("Contact saved on mission and client. Reloading email preview…");
      // Reload missions cache + reload preview
      const miss = await api.get("/missions");
      const map = {};
      (miss.data || []).forEach((m) => { map[m.mission_id] = m; });
      setMissionsById(map);
      const updated = map[sendTarget.mission.mission_id];
      setSendTarget({ inv: sendTarget.inv, mission: updated });
      // Clear the error and refetch the preview
      setSendError(null);
      setPreviewLoading(true);
      try {
        const r = await api.get(`/invoices/${sendTarget.inv.invoice_id}/send-email/preview`);
        setEmailPreview(r.data);
        setEditedBody(r.data.body_text || "");
        setEditedSubject(r.data.subject || "");
        setEditedToEmail(r.data.to_email || "");
        setEditedCcList(Array.isArray(r.data.cc_emails) ? [...r.data.cc_emails] : []);
      } catch (e) {
        const detail = e?.response?.data?.detail;
        if (detail && typeof detail === "object" && detail.code) {
          setSendError(detail);
        } else {
          setSendError({ code: "preview_failed", message: "Preview reload failed — please close and reopen the dialog." });
        }
      } finally { setPreviewLoading(false); }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save the contact");
    } finally { setSavingContact(false); }
  };

  const submitSend = async () => {
    if (!sendTarget) return;
    setSending(true);
    setSendError(null);
    try {
      const payload = { body_text: editedBody, subject: editedSubject };
      // Iter-193s — only CEO can override To/CC. Backend re-checks the role.
      if (isCeo) {
        const trimmedTo = (editedToEmail || "").trim();
        if (trimmedTo && trimmedTo !== (emailPreview?.to_email || "")) {
          payload.to_email_override = trimmedTo;
        }
        const cleanedCc = editedCcList.map((e) => (e || "").trim()).filter(Boolean);
        const origCc = emailPreview?.cc_emails || [];
        const changed = cleanedCc.length !== origCc.length || cleanedCc.some((e, i) => e !== origCc[i]);
        if (changed) {
          payload.cc_emails_override = cleanedCc;
        }
      }
      const r = await api.post(`/invoices/${sendTarget.inv.invoice_id}/${sendTarget.mode === "resend" ? "resend-email" : "send-email"}`, payload);
      const ccLabel = (r.data.cc || []).join(", ") || "—";
      toast.success(
        sendTarget.mode === "resend"
          ? `Email renvoyé à ${r.data.to} (cc ${ccLabel})${r.data.cra_attached ? " · facture + CRA" : " · facture seule"}`
          : `Email sent to ${r.data.to} (cc ${ccLabel})`
      );
      setSendTarget(null);
      setEmailPreview(null);
      setEditedBody("");
      setEditedSubject("");
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code) {
        setSendError(detail);
      } else {
        setSendError({ code: "unknown", message: typeof detail === "string" ? detail : "Email failed to send. Try again." });
      }
    } finally { setSending(false); }
  };

  // Iter-240 — edit the MAD counter-value after payment (foreign currency).
  const submitMadEdit = async () => {
    if (!madEditTarget) return;
    const v = madEditVal ? parseFloat(madEditVal) : null;
    if (v !== null && (!Number.isFinite(v) || v <= 0)) { toast.error("Montant MAD invalide"); return; }
    setMadSaving(true);
    try {
      await api.post(`/invoices/${madEditTarget.invoice_id}/paid-amount-mad`, { paid_amount_mad: v });
      toast.success(v === null ? "Contre-valeur MAD effacée (taux Settings appliqué)" : "Contre-valeur MAD enregistrée");
      setMadEditTarget(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec");
    } finally { setMadSaving(false); }
  };

  // Iter-240 — export the currently filtered list (CSV/xlsx/PDF) or selected ZIP.
  const downloadBlob = (data, filename) => {
    const url = URL.createObjectURL(new Blob([data]));
    const a = document.createElement("a");
    a.href = url; a.download = filename; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 4000);
  };
  const exportList = async (fmt) => {
    const ids = items.filter(matchesAllFilters).map((x) => x.invoice_id);
    if (ids.length === 0) { toast.error("Aucune facture à exporter pour ce filtre."); return; }
    setExporting(fmt);
    try {
      const r = await api.post("/invoices/export", { invoice_ids: ids, format: fmt }, { responseType: "blob" });
      downloadBlob(r.data, `factures_${new Date().toISOString().slice(0, 10)}.${fmt}`);
      toast.success(`Export ${fmt.toUpperCase()} — ${ids.length} facture(s)`);
    } catch (e) {
      toast.error("Export échoué");
    } finally { setExporting(null); }
  };
  const exportZip = async () => {
    const ids = [...selectedIds];
    if (ids.length === 0) { toast.error("Sélectionnez au moins une facture."); return; }
    setExporting("zip");
    try {
      const r = await api.post("/invoices/export-zip", { invoice_ids: ids }, { responseType: "blob" });
      downloadBlob(r.data, `factures_${new Date().toISOString().slice(0, 10)}.zip`);
      toast.success(`ZIP — ${ids.length} facture(s)`);
      setSelectMode(false); setSelectedIds(new Set());
    } catch (e) {
      let msg = "Export ZIP échoué";
      try { msg = JSON.parse(await e?.response?.data?.text())?.detail || msg; } catch { /* noop */ }
      toast.error(msg);
    } finally { setExporting(null); }
  };
  const toggleSelected = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const openMarkPaid = (inv) => {    setPayTarget(inv);
    setPayProofFile(null);
    setPayForm({
      paid_date: new Date().toISOString().slice(0, 10),
      paid_amount: String(inv.total_ttc || ""),
      paid_amount_mad: inv.paid_amount_mad ? String(inv.paid_amount_mad) : "",
      // Iter-211 — payment details captured at mark-paid time.
      payment_mode: "VIREMENT",
      payment_reference: "",
      payment_comment: "",
    });
  };

  const submitMarkPaid = async () => {
    if (!payTarget) return;
    // Iter-211 — required client-side: mode + reference (mirrors Déclaration de paiement).
    if (!payForm.payment_mode) { toast.error("Mode de paiement requis"); return; }
    if (!(payForm.payment_reference || "").trim()) { toast.error("Référence du paiement requise"); return; }
    if (payProofFile && payProofFile.size > 15 * 1024 * 1024) { toast.error("Justificatif trop volumineux (max 15 Mo)"); return; }
    setMarkingPaid(true);
    try {
      await api.post(`/invoices/${payTarget.invoice_id}/mark-paid`, {
        paid_date: payForm.paid_date,
        paid_amount: parseFloat(payForm.paid_amount) || payTarget.total_ttc,
        paid_amount_mad: payForm.paid_amount_mad ? parseFloat(payForm.paid_amount_mad) : null,
        payment_mode: payForm.payment_mode,
        payment_reference: (payForm.payment_reference || "").trim(),
        payment_comment: (payForm.payment_comment || "").trim() || null,
      });
      // Iter-233 — optional payment proof (PDF/image) → Drive « Swift » folder.
      if (payProofFile) {
        try {
          const fd = new FormData();
          fd.append("file", payProofFile);
          await api.post(`/invoices/${payTarget.invoice_id}/payment-proof`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          toast.success(`${payTarget.number} marked as paid — justificatif archivé dans Drive « Swift »`);
        } catch (pe) {
          toast.warning(`Paiement enregistré, mais l'upload du justificatif a échoué : ${pe?.response?.data?.detail || "erreur"}`);
        }
      } else {
        toast.success(`${payTarget.number} marked as paid`);
      }
      setPayTarget(null);
      setPayProofFile(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setMarkingPaid(false); }
  };

  const markUnpaid = async (inv) => {
    if (!window.confirm(`Cancel payment for ${inv.number}?`)) return;
    try {
      await api.post(`/invoices/${inv.invoice_id}/mark-unpaid`);
      toast.success("Payment cancelled");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  // Iter-206p — manual resend of a sent invoice email + CRA. Uses persisted PDFs.
  // Iter-228x — opens the same editable Send dialog as the initial send so the
  // CEO can review/edit To, Cc, Subject, body, and the attachment list before
  // resending. The submit handler picks /resend-email vs /send-email based on
  // sendTarget.mode.
  const [resendingId, setResendingId] = useState(null);
  const openResendDialog = (inv) => openSend(inv, "resend");

  // Iter-206p / Iter-206s — open the persisted Monthly CRA PDF in a new tab.
  // We use axios + blob URL so the request carries the auth context (header +
  // cookie) which a raw `<a href>` cannot do — `<a href>` triggers a browser
  // navigation that gets 401 → SPA falls back to home page.
  const [viewingCraId, setViewingCraId] = useState(null);
  const viewInvoiceCRA = async (inv) => {
    setViewingCraId(inv.invoice_id);
    try {
      const r = await api.get(`/invoices/${inv.invoice_id}/cra-pdf`, {
        responseType: "blob",
        params: { v: inv.cra_pdf_built_at || Date.now() },
      });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      window.open(url, "_blank", "noopener,noreferrer");
      // Revoke after a short delay so the new tab has time to read the blob.
      setTimeout(() => window.URL.revokeObjectURL(url), 30000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec d'ouverture du CRA");
    } finally {
      setViewingCraId(null);
    }
  };
  // When the backend cannot auto-resolve the roles (BC never uploaded), we open
  // the standalone audit page where the CEO can pick the roles manually.
  const [regeneratingCraId, setRegeneratingCraId] = useState(null);
  const regenerateInvoiceCRA = async (inv) => {
    if (!window.confirm(`Régénérer le Monthly CRA pour la facture ${inv.number} ?\n\nLe CRA sera filtré pour ne contenir que les profils du BC ${inv.po_number || "concerné"}.`)) return;
    setRegeneratingCraId(inv.invoice_id);
    try {
      const r = await api.post(`/invoices/${inv.invoice_id}/regenerate-cra`);
      const roles = (r.data?.po_role_names || []).join(", ") || "tous";
      toast.success(`CRA régénéré (rôles : ${roles})`);
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d && typeof d === "object" && d.code === "po_role_names_unresolved") {
        toast.warning("Sélection manuelle des rôles requise — ouverture de l'audit CRA…");
        setTimeout(() => { window.location.href = "/admin/cra-scope-audit"; }, 800);
      } else {
        toast.error(typeof d === "string" ? d : (d?.message || "Échec"));
      }
    } finally {
      setRegeneratingCraId(null);
    }
  };

  // Iter-206t — physically delete the persisted CRA file + clear DB references.
  // Useful when "Régen CRA" keeps producing the same wide CRA (the line_roles
  // fallback resolves to the same legacy multi-BC role list).
  const [deletingCraId, setDeletingCraId] = useState(null);
  const deleteInvoiceCRA = async (inv) => {
    if (!window.confirm(`Supprimer physiquement le Monthly CRA persisté pour la facture ${inv.number} ?\n\nLe fichier sera retiré du disque et les références (po_role_names, cra_pdf_path) effacées. Vous pourrez ensuite régénérer un CRA depuis zéro (et utiliser la sélection manuelle des rôles depuis "CRA Scope Audit" si nécessaire).`)) return;
    setDeletingCraId(inv.invoice_id);
    try {
      const r = await api.delete(`/invoices/${inv.invoice_id}/cra-pdf`);
      toast.success(`CRA supprimé${r.data?.file_deleted ? " (fichier retiré du disque)" : ""}. Cliquez "Régen CRA" pour reconstruire.`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de suppression");
    } finally {
      setDeletingCraId(null);
    }
  };

  // Iter-195o/s — CEO toggle to pause/resume automatic payment reminders on
  // a single invoice. The scope param ("ALL"/"MILESTONE"/"OVERDUE") lets the
  // CEO silence only a subset.
  const toggleInvoiceReminders = async (inv, scope = "ALL") => {
    const isPaused = !!(inv.payment_reminders_paused || inv.payment_reminders_paused_milestones || inv.payment_reminders_paused_overdue);
    if (!isPaused) {
      // Pause request — confirm with scope.
      const labelMap = { ALL: "Milestones + Dépassement", MILESTONE: "Milestones uniquement", OVERDUE: "Dépassement uniquement" };
      if (!window.confirm(`Désactiver les relances "${labelMap[scope]}" pour la facture ${inv.number || inv.invoice_id} ?`)) return;
      try {
        await api.patch(`/invoices/${inv.invoice_id}/reminders-paused`, { paused: true, scope });
        toast.success(`Relances en pause (${labelMap[scope]})`);
        load();
      } catch (e) { toast.error(e?.response?.data?.detail || "Échec"); }
    } else {
      try {
        await api.patch(`/invoices/${inv.invoice_id}/reminders-paused`, { paused: false });
        toast.success("Relances automatiques réactivées");
        load();
      } catch (e) { toast.error(e?.response?.data?.detail || "Échec"); }
    }
  };

  const openCancel = (inv) => {
    setCancelTarget(inv);
    setCancelReason("");
  };

  const submitCancel = async () => {
    if (!cancelTarget) return;
    const reason = (cancelReason || "").trim();
    if (!reason) {
      toast.error("Cancellation reason is required");
      return;
    }
    setCancelling(true);
    try {
      await api.post(`/invoices/${cancelTarget.invoice_id}/cancel`, { reason });
      toast.success(`${cancelTarget.number} cancelled — number reserved (ANNULÉE stamp added to PDF)`);
      setCancelTarget(null);
      setCancelReason("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setCancelling(false);
    }
  };

  return (
    <div className="p-4 md:p-8 lg:p-12 max-w-[1400px]">
      <div className="mb-10">
        <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-3 font-mono">Finance</div>
        <h1 className="font-heading text-2xl md:text-4xl font-bold tracking-tight">Invoices</h1>
        <p className="text-zinc-500 mt-3 max-w-xl">Each generated invoice is archived in Drive. Mark it <strong>Sent</strong> as soon as it is sent to the client (this unblocks the corresponding freelance invoice), then <strong>Paid</strong> once payment is received.</p>
      </div>

      {/* Iter-196 — Surface the upcoming auto-invoice forecast at the top of
          the Invoices page so the CEO sees what's about to fire BEFORE the
          historical list. Hidden for non-CEO roles (the underlying endpoint
          requires CEO scope anyway). The cash-flow sidebar on the right
          shows expected receivables for the next 30 days. */}
      {currentUser?.role === "CEO" && (
        <div
          className="mb-6 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr),320px] gap-4 items-start"
          data-testid="invoices-forecast-wrap"
        >
          <div className="min-w-0">
            <AutoInvoiceForecastWidget />
          </div>
          <UpcomingReceivablesWidget days={30} />
        </div>
      )}

      {/* Iter-247 — retroactive invoices: AI scan or manual entry */}
      <div className="mb-6">
        <RetroInvoiceBlock onCreated={load} editInvoice={retroEdit} onEditClose={() => setRetroEdit(null)} />
      </div>

      <div className="bg-white border border-zinc-200 rounded-lg overflow-x-auto md:overflow-hidden">
        {/* Iter-230 — single-select status filter with live counts.
            Default = "En cours" (à envoyer + envoyée + délai dépassé).
            "Annulée" acts as an archive: cancelled invoices are hidden
            everywhere else, including "Toutes". */}
        <div className="px-4 md:px-5 py-3 border-b border-zinc-200 flex flex-wrap items-center gap-2" data-testid="invoices-status-filter">
          {(() => {
            const countFor = (k) => items.filter((inv) => {
              if (!matchesAdvancedFilters(inv)) return false;
              const s = invoiceStatusKey(inv);
              if (k === "ongoing") return s === "draft" || s === "sent" || s === "overdue";
              if (k === "all") return s !== "cancelled";
              return s === k;
            }).length;
            return [
              { k: "ongoing", label: "En cours", cls: "bg-zinc-900 text-white border-zinc-900" },
              { k: "draft", label: "À envoyer", cls: "bg-amber-100 text-amber-800 border-amber-200" },
              { k: "sent", label: "Envoyée", cls: "bg-blue-100 text-blue-800 border-blue-200" },
              { k: "overdue", label: "Délai dépassé", cls: "bg-red-100 text-red-800 border-red-200" },
              { k: "paid", label: "Payée", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
              { k: "cancelled", label: "Annulée", cls: "bg-zinc-200 text-zinc-700 border-zinc-300" },
              { k: "all", label: "Toutes", cls: "bg-zinc-100 text-zinc-800 border-zinc-300" },
            ].map((opt) => {
              const active = statusFilter === opt.k;
              return (
                <button
                  key={opt.k}
                  onClick={() => setStatusFilter(opt.k)}
                  data-testid={`invoices-filter-${opt.k}`}
                  className={`px-2.5 h-7 text-[11px] font-mono uppercase tracking-wider rounded border transition-colors inline-flex items-center gap-1.5 ${
                    active ? opt.cls + " ring-1 ring-offset-1 ring-zinc-400" : "border-zinc-200 text-zinc-500 hover:bg-zinc-50"
                  }`}
                >
                  {opt.label}
                  <span className={`text-[10px] tabular-nums ${active ? "opacity-80" : "text-zinc-400"}`}>
                    {countFor(opt.k)}
                  </span>
                </button>
              );
            });
          })()}
          {/* Iter-194w — Bulk weekly relance session button (CEO). */}
          <BulkReminderButton onDone={load} />
          {/* Iter-240 — exports (filtered list) + ZIP selection mode */}
          <div className="flex items-center gap-1 ml-2" data-testid="invoices-export-bar">
            {["csv", "xlsx", "pdf"].map((f) => (
              <button
                key={f}
                onClick={() => exportList(f)}
                disabled={!!exporting}
                data-testid={`invoices-export-${f}`}
                title={`Exporter la liste filtrée en ${f.toUpperCase()}`}
                className="inline-flex items-center gap-1 px-2 h-7 border border-zinc-200 text-[10px] font-mono uppercase text-zinc-600 rounded-md hover:bg-zinc-50 disabled:opacity-50"
              >
                {exporting === f ? <Loader2 size={10} className="animate-spin" /> : <Download size={10} />} {f === "xlsx" ? "Excel" : f.toUpperCase()}
              </button>
            ))}
            <button
              onClick={() => { setSelectMode((v) => !v); setSelectedIds(new Set()); }}
              data-testid="invoices-select-mode-toggle"
              title="Sélectionner des factures pour un export ZIP des PDFs"
              className={`inline-flex items-center gap-1 px-2 h-7 border text-[10px] font-mono uppercase rounded-md ${selectMode ? "border-indigo-300 bg-indigo-50 text-indigo-800" : "border-zinc-200 text-zinc-600 hover:bg-zinc-50"}`}
            >
              <Archive size={10} /> {selectMode ? "Annuler sélection" : "Sélection ZIP"}
            </button>
            {selectMode && (
              <>
                <button
                  onClick={() => setSelectedIds(new Set(items.filter(matchesAllFilters).map((x) => x.invoice_id)))}
                  data-testid="invoices-select-all"
                  className="inline-flex items-center gap-1 px-2 h-7 border border-zinc-200 text-[10px] font-mono uppercase text-zinc-600 rounded-md hover:bg-zinc-50"
                >
                  <CheckSquare size={10} /> Tout
                </button>
                <button
                  onClick={exportZip}
                  disabled={selectedIds.size === 0 || !!exporting}
                  data-testid="invoices-export-zip"
                  className="inline-flex items-center gap-1 px-2.5 h-7 bg-zinc-950 text-white text-[10px] font-mono uppercase rounded-md hover:bg-zinc-800 disabled:opacity-50"
                >
                  {exporting === "zip" ? <Loader2 size={10} className="animate-spin" /> : <Archive size={10} />} Exporter ZIP ({selectedIds.size})
                </button>
              </>
            )}
          </div>
          <span className="ml-auto text-[10px] text-zinc-500 font-mono" data-testid="invoices-count">
            {items.filter(matchesAllFilters).length} facture{items.filter(matchesAllFilters).length > 1 ? "s" : ""}
          </span>
        </div>
        {/* Iter-241 — advanced filters row (client / currency / sent-date range) */}
        <div className="px-4 md:px-5 py-2 border-b border-zinc-200 bg-zinc-50/50 flex flex-wrap items-center gap-2" data-testid="invoices-advanced-filters">
          <select
            value={clientFilter} onChange={(e) => setClientFilter(e.target.value)}
            data-testid="invoices-filter-client"
            className={`h-7 px-1.5 text-[11px] font-mono border rounded-md bg-white max-w-[220px] ${clientFilter ? "border-indigo-300 text-indigo-900" : "border-zinc-200 text-zinc-600"}`}
          >
            <option value="">Client : tous</option>
            {[...new Set(items.map((x) => x.client_name || "—"))].sort().map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
          <select
            value={currencyFilter} onChange={(e) => setCurrencyFilter(e.target.value)}
            data-testid="invoices-filter-currency"
            className={`h-7 px-1.5 text-[11px] font-mono border rounded-md bg-white ${currencyFilter ? "border-indigo-300 text-indigo-900" : "border-zinc-200 text-zinc-600"}`}
          >
            <option value="">Devise : toutes</option>
            {[...new Set(items.map((x) => (x.currency || "MAD").toUpperCase()))].sort().map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
          <span className="text-[10px] font-mono uppercase text-zinc-400 ml-1">Envoyée du</span>
          <input
            type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
            data-testid="invoices-filter-date-from"
            className={`h-7 px-1.5 text-[11px] font-mono border rounded-md bg-white ${dateFrom ? "border-indigo-300 text-indigo-900" : "border-zinc-200 text-zinc-600"}`}
          />
          <span className="text-[10px] font-mono uppercase text-zinc-400">au</span>
          <input
            type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
            data-testid="invoices-filter-date-to"
            className={`h-7 px-1.5 text-[11px] font-mono border rounded-md bg-white ${dateTo ? "border-indigo-300 text-indigo-900" : "border-zinc-200 text-zinc-600"}`}
          />
          {advancedActive && (
            <button
              onClick={resetAdvancedFilters}
              data-testid="invoices-filter-reset"
              className="inline-flex items-center gap-1 px-2 h-7 border border-zinc-300 text-[10px] font-mono uppercase text-zinc-600 rounded-md hover:bg-zinc-100"
            >
              <X size={10} /> Réinitialiser
            </button>
          )}
        </div>

        <div className="hidden md:grid grid-cols-12 px-5 py-3 text-[10px] uppercase tracking-[0.2em] text-zinc-500 border-b border-zinc-200 font-mono">
          <div className="col-span-2">N° Invoice</div>
          <div className="col-span-3">Client</div>
          <div className="col-span-3">Mission · Période</div>
          <div className="col-span-2">Envoi / Échéance</div>
          <div className="col-span-1 text-right pr-3">TTC</div>
          <div className="col-span-1">Statut</div>
        </div>
        {items.length === 0 && (
          <div className="px-5 py-16 text-sm text-zinc-500 text-center">No invoice generated yet.</div>
        )}
        {items.length > 0 && items.filter(matchesAllFilters).length === 0 && (
          <div className="px-5 py-16 text-sm text-zinc-500 text-center" data-testid="invoices-empty-filter">
            Aucune facture pour ce filtre.
          </div>
        )}
        {items.filter(matchesAllFilters).map((i) => (
          <div key={i.invoice_id} className="block md:grid md:grid-cols-12 md:items-center px-4 md:px-5 py-3 md:py-4 border-b border-zinc-100 last:border-b-0 hover:bg-zinc-50" data-testid={`invoices-row-${i.invoice_id}`}>
            <div className="md:col-span-2 font-mono font-medium text-sm flex items-center gap-2 min-w-0 flex-wrap">
              {selectMode && (
                <button
                  onClick={() => toggleSelected(i.invoice_id)}
                  data-testid={`invoices-select-${i.invoice_id}`}
                  title="Sélectionner pour l'export ZIP"
                  className="shrink-0 text-zinc-500 hover:text-zinc-900"
                >
                  {selectedIds.has(i.invoice_id) ? <CheckSquare size={15} className="text-indigo-600" /> : <Square size={15} />}
                </button>
              )}
              <FileText size={14} className="text-zinc-500 shrink-0" />
              <span className="truncate">{i.number}</span>
              {(i.retro || i.source === "RETRO") && (
                <>
                  <span data-testid={`retro-badge-${i.invoice_id}`} title="Facture rétroactive (scan/saisie manuelle)" className="shrink-0 px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wide bg-violet-50 text-violet-700 border border-violet-200 rounded">
                    Rétro
                  </span>
                  {!i.cancelled && (
                    <button
                      onClick={(e) => { e.stopPropagation(); setRetroEdit(i); }}
                      data-testid={`retro-edit-btn-${i.invoice_id}`}
                      title="Modifier cette facture rétroactive (même payée) pour corriger une erreur"
                      className="shrink-0 text-violet-600 hover:text-violet-900"
                    >
                      <Pencil size={12} />
                    </button>
                  )}
                  {Array.isArray(i.retro_edit_history) && i.retro_edit_history.length > 0 && (
                    <button
                      onClick={(e) => { e.stopPropagation(); setRetroHistoryTarget(i); }}
                      data-testid={`retro-history-btn-${i.invoice_id}`}
                      title={`${i.retro_edit_history.length} correction(s) — voir l'historique`}
                      className="shrink-0 text-zinc-400 hover:text-zinc-800"
                    >
                      <HistoryIcon size={12} />
                    </button>
                  )}
                </>
              )}
              {i.drive_web_view_link && (
                <a href={i.drive_web_view_link} target="_blank" rel="noreferrer" data-testid={`drive-link-${i.invoice_id}`} className="text-blue-700 shrink-0">
                  <ExternalLink size={11} />
                </a>
              )}
              {/* Iter-229 — forfait milestone attachments (livrable + autres
                  documents) consultables directly from the invoice row. */}
              {Array.isArray(i.attachments) && i.attachments.map((a, ai) => (
                <a
                  key={`att-${ai}`}
                  href={a.web_view_link || "#"}
                  target="_blank" rel="noreferrer"
                  data-testid={`invoice-attachment-${i.invoice_id}-${ai}`}
                  title={(a.kind === "deliverable" ? "Livrable : " : "Document : ") + (a.name || "")}
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-mono rounded bg-blue-50 text-blue-800 border border-blue-200 hover:bg-blue-100 shrink-0 max-w-[150px]"
                >
                  <Paperclip size={9} className="shrink-0" /> <span className="truncate">{a.name || "Fichier"}</span>
                </a>
              ))}
              {/* Iter-195p/s — visible badge if reminders are paused on this invoice (any scope) */}
              <RemindersPausedBadge
                row={i}
                kind="invoice"
                id={i.invoice_id}
                canReactivate={currentUser?.role === "CEO"}
                onChanged={load}
              />
              {/* Iter-228w-bis — bank account drift warning. Visible only on
                  unpaid + non-cancelled invoices whose bank snapshot differs
                  from the currently resolved account (default OR mission
                  override). Click → regenerate PDF with new RIB. */}
              {currentUser?.role === "CEO" && driftedIds.has(i.invoice_id) && !i.paid_at && !i.cancelled && (
                <button
                  onClick={() => regenerateBankDrifted(i)}
                  data-testid={`invoices-bank-drift-${i.invoice_id}`}
                  title="Les coordonnées bancaires ont changé depuis la génération de cette facture. Cliquez pour régénérer le PDF avec le nouveau RIB."
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-mono uppercase rounded bg-amber-100 text-amber-900 border border-amber-200 hover:bg-amber-200 transition-colors shrink-0"
                >
                  Compte modifié — régénérer ?
                </button>
              )}
              <span className="md:hidden ml-auto"><StatusBadge inv={i} /></span>
            </div>
            {/* Mobile-only summary line */}
            <div className="md:hidden mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] font-mono text-zinc-600">
              <span className="truncate max-w-[55%]">{i.client_name}</span>
              <span className="text-zinc-400">·</span>
              <span>{MONTHS[i.month - 1]} {i.year}</span>
              <span className="text-zinc-400">·</span>
              <span className="font-medium">{fmt(i.total_ttc)} {i.currency}</span>
            </div>
            {/* Iter-196f — Mobile dates strip. The same info that fills the
                desktop "InvoiceDateStrip" column is reproduced here so the
                user sees envoi/échéance/délai/relances directly on the card,
                without having to switch to landscape or zoom in. */}
            <div className="md:hidden mt-1">
              <InvoiceDateStrip inv={i} onReload={load} />
            </div>
            <div className="hidden md:block md:col-span-3 text-sm truncate">{i.client_name}</div>
            <div className="hidden md:block md:col-span-3 text-sm truncate" data-testid={`invoices-mission-${i.invoice_id}`}>
              <div className="truncate">{i.mission_title}</div>
              <div className="text-[10px] font-mono text-zinc-500">{MONTHS[i.month - 1]} {i.year}</div>
            </div>
            <div className="hidden md:block md:col-span-2"><InvoiceDateStrip inv={i} onReload={load} /></div>
            <div className="hidden md:block md:col-span-1 text-right font-mono text-sm pr-3 whitespace-nowrap">{fmt(i.total_ttc)} <span className="text-[10px] text-zinc-500">{i.currency}</span></div>
            <div className="hidden md:block md:col-span-1"><StatusBadge inv={i} /></div>
            {/* Iter-207b — Phase 4 : actions toolbar moved BELOW the row,
                full-width horizontal, scroll on small screens. Prevents the
                right-side "agglomération" of buttons in the old col-span-2 cell. */}
            <div className="md:col-span-12 mt-2 md:mt-2 md:pt-2 md:border-t md:border-zinc-100 flex items-center justify-start gap-1.5 flex-wrap overflow-x-auto" data-testid={`invoices-actions-${i.invoice_id}`}>
              {/* ───────── Cluster A — CONSULTATION ───────── */}
              {!i.cancelled && (
                <IconAction icon={Eye} testid={`invoices-view-${i.invoice_id}`} label="Voir le PDF de la facture" onClick={() => setViewInv(i)} />
              )}
              {/* Iter-207d — single canonical "Voir CRA" button (the persisted CRA via
                  authenticated blob; reflects the latest regen). Avoids the previous
                  confusion with two distinct "CRA" buttons (sent attachment vs persisted). */}
              {!i.cancelled && (
                <IconAction icon={FileText} testid={`invoices-view-cra-${i.invoice_id}`} label="Voir le Monthly CRA" loading={viewingCraId === i.invoice_id} onClick={() => viewInvoiceCRA(i)} />
              )}
              {/* Iter-200c — paid via client declaration → quick link to the declaration. */}
              {!i.cancelled && i.paid_at && (i.paid_via === "client_self_declared" || i.paid_via === "ceo_validated_declaration") && (
                <IconAction icon={ExternalLink} tone="blueSoft" href={`/admin/payment-declarations?invoice=${i.invoice_id}`} testid={`invoices-view-declaration-${i.invoice_id}`} label="Voir la déclaration de paiement du client" />
              )}

              {/* ───────── Separator → COMMUNICATION CLIENT ───────── */}
              {!i.cancelled && (i.sent_at || (!i.sent_at && !i.paid_at)) && (
                <div className="w-px h-5 bg-zinc-200 mx-1" aria-hidden="true" />
              )}

              {/* Cluster B — COMMUNICATION CLIENT (Send / Resend / Relancer / Pause reminders) */}
              {!i.cancelled && !i.sent_at && !i.paid_at && (
                <IconAction icon={Mail} tone="primary" testid={`invoices-send-${i.invoice_id}`} label="Envoyer la facture par email au client" onClick={() => openSend(i)} />
              )}
              {!i.cancelled && i.sent_at && (
                <IconAction icon={Send} testid={`invoices-resend-email-${i.invoice_id}`} label="Renvoyer l'email (To/Cc/sujet/corps modifiables)" loading={resendingId === i.invoice_id} onClick={() => openResendDialog(i)} />
              )}
              {!i.cancelled && !i.paid_at && i.sent_at && (
                <IconAction icon={Bell} tone={i.payment_overdue ? "redSoft" : "amberSoft"} testid={`invoices-manual-reminder-${i.invoice_id}`} label={i.payment_overdue ? "Relancer le client (paiement en retard)" : "Relancer le client (rappel d'échéance)"} onClick={() => setManualReminderInvoice(i)} />
              )}
              {!i.cancelled && !i.paid_at && i.sent_at && currentUser?.role === "CEO" && (
                (i.payment_reminders_paused || i.payment_reminders_paused_milestones || i.payment_reminders_paused_overdue) ? (
                  <IconAction icon={Play} testid={`invoices-reminders-pause-${i.invoice_id}`} label="Relances en pause — cliquer pour les réactiver" onClick={() => toggleInvoiceReminders(i)} />
                ) : (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        data-testid={`invoices-reminders-pause-${i.invoice_id}`}
                        title="Désactiver les relances automatiques sur cette facture"
                        className="inline-flex items-center gap-1 px-2 h-8 text-xs rounded-md border border-zinc-200 text-zinc-600 hover:bg-zinc-50"
                      >
                        <Pause size={11} />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => toggleInvoiceReminders(i, "MILESTONE")} data-testid={`invoices-reminders-pause-milestone-${i.invoice_id}`}>
                        Pause Milestones uniquement
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => toggleInvoiceReminders(i, "OVERDUE")} data-testid={`invoices-reminders-pause-overdue-${i.invoice_id}`}>
                        Pause Dépassement uniquement
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => toggleInvoiceReminders(i, "ALL")} data-testid={`invoices-reminders-pause-all-${i.invoice_id}`}>
                        Pause complète (les deux)
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )
              )}

              {/* ───────── Separator → ENCAISSEMENT ───────── */}
              {!i.cancelled && (i.sent_at || i.paid_at) && (
                <div className="w-px h-5 bg-zinc-200 mx-1" aria-hidden="true" />
              )}

              {/* Cluster C — ENCAISSEMENT (Mark paid / Undo paid) */}
              {!i.cancelled && !i.paid_at && (
                <IconAction icon={CheckCircle2} tone="success" disabled={!i.sent_at} testid={`invoices-mark-paid-${i.invoice_id}`} label={i.sent_at ? "Marquer comme payée" : "La facture doit d'abord être envoyée"} onClick={() => openMarkPaid(i)} />
              )}
              {!i.cancelled && i.paid_at && (
                <IconAction icon={RotateCcw} testid={`invoices-mark-unpaid-${i.invoice_id}`} label="Annuler le paiement" onClick={() => markUnpaid(i)} />
              )}
              {!i.cancelled && i.paid_at && (
                <span data-testid={`invoices-paid-date-${i.invoice_id}`} title="Date de paiement" className="inline-flex items-center gap-1 px-2 h-8 text-[11px] font-mono text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md">
                  <CheckCircle2 size={11} /> Payée le {(i.paid_at || "").slice(0, 10)}
                </span>
              )}
              {!i.cancelled && i.paid_at && (i.currency || "MAD") !== "MAD" && (
                <button
                  onClick={() => { setMadEditTarget(i); setMadEditVal(i.paid_amount_mad ? String(i.paid_amount_mad) : ""); }}
                  data-testid={`invoices-edit-mad-${i.invoice_id}`}
                  title={i.paid_amount_mad
                    ? `Contre-valeur : ${Number(i.paid_amount_mad).toLocaleString("fr-FR", { minimumFractionDigits: 2 })} MAD — cliquer pour modifier`
                    : "Aucune contre-valeur MAD saisie (taux Settings appliqué) — cliquer pour renseigner"}
                  className={`inline-flex items-center gap-1 px-2 h-8 border text-[11px] font-mono rounded-md ${i.paid_amount_mad ? "border-zinc-200 text-zinc-600 hover:bg-zinc-50" : "border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100"}`}
                >
                  <Pencil size={10} /> {i.paid_amount_mad ? `${Number(i.paid_amount_mad).toLocaleString("fr-FR")} MAD` : "MAD ?"}
                </button>
              )}
              {!i.cancelled && i.paid_at && i.payment_proof?.drive_web_view_link && (
                <IconAction icon={Paperclip} tone="skySoft" href={i.payment_proof.drive_web_view_link} target="_blank" testid={`invoices-payment-proof-${i.invoice_id}`} label={`Voir le justificatif de paiement${i.payment_proof.filename ? ` — ${i.payment_proof.filename}` : ""}`} />
              )}

              {/* ───────── Separator → MAINTENANCE CRA (CEO/ADMIN) ───────── */}
              {!i.cancelled && (currentUser?.role === "CEO" || currentUser?.role === "ADMIN") && (
                <div className="w-px h-5 bg-zinc-200 mx-1" aria-hidden="true" />
              )}

              {/* Cluster D — MAINTENANCE CRA (Régen + Suppr — CEO/ADMIN seulement) */}
              {!i.cancelled && (currentUser?.role === "CEO" || currentUser?.role === "ADMIN") && (
                <IconAction icon={RefreshCw} testid={`invoices-regen-cra-${i.invoice_id}`} label={i.paid_at ? "Facture payée — le CRA ne peut plus être régénéré" : "Régénérer le Monthly CRA"} loading={regeneratingCraId === i.invoice_id} disabled={!!i.paid_at} onClick={() => regenerateInvoiceCRA(i)} />
              )}
              {!i.cancelled && (currentUser?.role === "CEO" || currentUser?.role === "ADMIN") && i.cra_pdf_filename && (
                <IconAction icon={Trash2} tone="danger" testid={`invoices-delete-cra-${i.invoice_id}`} label="Supprimer le Monthly CRA (fichier + références)" loading={deletingCraId === i.invoice_id} onClick={() => deleteInvoiceCRA(i)} />
              )}

              {/* ───────── Separator → FREELANCES (compteur déblocage) ───────── */}
              {!i.cancelled && i.sent_at && i.freelance_unlock_status && i.freelance_unlock_status !== "N/A" && (
                <div className="w-px h-5 bg-zinc-200 mx-1" aria-hidden="true" />
              )}

              {/* Cluster E — FREELANCES (statut + actions de déblocage) */}
              {!i.cancelled && canPauseFreelance && i.sent_at && i.freelance_unlock_status && i.freelance_unlock_status !== "N/A" && i.freelance_unlock_status !== "UNLOCKED" && i.freelance_unlock_status !== "CANCELLED" && (
                <IconAction icon={i.freelance_unlock_status === "PAUSED" ? Play : Pause} tone={i.freelance_unlock_status === "PAUSED" ? "amberSoft" : "neutral"} testid={`invoices-freelance-${i.freelance_unlock_status === "PAUSED" ? "unpause" : "pause"}-${i.invoice_id}`} label={i.freelance_unlock_status === "PAUSED" ? "Relancer le compteur de déblocage freelance" : "Mettre en pause le compteur de déblocage freelance"} onClick={() => togglePauseUnlock(i)} />
              )}
              {!i.cancelled && currentUser?.role === "CEO" && i.sent_at && i.freelance_unlock_status === "PENDING" && (
                <IconAction icon={Zap} tone="emeraldSoft" testid={`invoices-freelance-force-unlock-${i.invoice_id}`} label="Débloquer les freelances maintenant (ignore le délai restant)" onClick={() => forceUnlockNow(i)} />
              )}
              {i.sent_at && i.freelance_unlock_status && (
                <span
                  data-testid={`invoices-freelance-unlock-status-${i.invoice_id}`}
                  title={`Statut compteur freelance · ${i.freelance_unlock_status}${i.freelance_unlock_target_at ? " · cible " + i.freelance_unlock_target_at.slice(0,10) : ""} · ${(i.freelance_impacted_user_ids || []).length} freelance(s)`}
                  className={`inline-flex items-center gap-1 px-2 h-7 text-[10px] font-mono rounded ${
                    i.freelance_unlock_status === "UNLOCKED" ? "bg-emerald-100 text-emerald-800" :
                    i.freelance_unlock_status === "PAUSED" ? "bg-amber-100 text-amber-900" :
                    i.freelance_unlock_status === "CANCELLED" ? "bg-zinc-200 text-zinc-700 line-through" :
                    i.freelance_unlock_status === "N/A" ? "bg-zinc-100 text-zinc-500 italic" :
                    "bg-blue-100 text-blue-800"
                  }`}
                >
                  {i.freelance_unlock_status === "N/A" ? "Pas de FL" : i.freelance_unlock_status}
                </span>
              )}
              {i.sent_at && i.freelance_unlock_status === "PENDING" && typeof i.freelance_unlock_days_remaining === "number" && (
                <span
                  data-testid={`invoices-freelance-countdown-${i.invoice_id}`}
                  title={i.freelance_unlock_overdue
                    ? "Délai écoulé — déblocage à la prochaine exécution du cron horaire"
                    : `${i.freelance_unlock_days_remaining} jour(s) avant déblocage auto`}
                  className={`inline-flex items-center gap-1 px-2 h-7 text-[10px] font-mono rounded ${
                    i.freelance_unlock_overdue ? "bg-emerald-50 text-emerald-800 border border-emerald-200" : "bg-blue-50 text-blue-800 border border-blue-200"
                  }`}
                >
                  <Clock size={10} />
                  {i.freelance_unlock_overdue ? "Ready" : `${i.freelance_unlock_days_remaining}j`}
                </span>
              )}

              {/* ───────── Cluster F — DESTRUCTIF (Cancel — pushed to far right via ml-auto) ───────── */}
              {!i.cancelled && canCancel && !i.paid_at && (
                <IconAction icon={Ban} tone="danger" className="ml-auto" testid={`invoices-cancel-${i.invoice_id}`} label="Annuler la facture (marquée ANNULÉE, numéro conservé)" onClick={() => openCancel(i)} />
              )}
              {i.cancelled && (
                <span className="text-[11px] text-zinc-500 italic" data-testid={`invoice-cancelled-note-${i.invoice_id}`}>Number reserved</span>
              )}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!viewInv} onOpenChange={(o) => !o && setViewInv(null)}>
        <DialogContent className="max-w-6xl w-[95vw] lg:w-[80vw] h-[85vh] p-0 overflow-hidden flex flex-col gap-0" data-testid="invoice-pdf-dialog">
          <DialogHeader className="px-5 py-3 border-b border-brand-navy/15 shrink-0 space-y-0.5">
            <DialogTitle className="flex items-center gap-3 text-sm">
              <span className="font-mono">{viewInv?.number}</span>
              {viewInv?.drive_web_view_link && (
                <a href={viewInv.drive_web_view_link} target="_blank" rel="noreferrer" className="text-xs text-blue-700 inline-flex items-center gap-1">
                  <ExternalLink size={12} /> Ouvrir dans Drive
                </a>
              )}
              {viewInv && !viewInv.cancelled && (
                <button
                  onClick={async () => {
                    if (!window.confirm(`Régénérer le PDF de ${viewInv.number} ?\n\nUtile si le contenu affiché ne correspond pas aux données réelles de la facture (ex. mauvais montant, TVA aberrante).`)) return;
                    try {
                      await api.post(`/invoices/${viewInv.invoice_id}/regenerate-pdf`);
                      toast.success("PDF régénéré — rechargement…");
                      // Iter-206g — force iframe to fetch fresh PDF by appending
                      // a cache-busting query param BEFORE the `#` hash. The
                      // previous attempt appended after the hash, which the
                      // browser ignored for caching purposes.
                      setPdfCacheBuster(Date.now());
                    } catch (e) {
                      toast.error(e?.response?.data?.detail || "Échec de la régénération");
                    }
                  }}
                  className="text-xs text-amber-700 inline-flex items-center gap-1 hover:underline"
                  data-testid={`invoice-regen-pdf-${viewInv.invoice_id}`}
                  title="Régénérer le PDF à partir des données de la facture (corrige un contenu obsolète)"
                >
                  <RefreshCw size={12} /> Régénérer le PDF
                </button>
              )}
            </DialogTitle>
            <DialogDescription className="text-[11px] text-zinc-500 m-0">Read-only preview — archived in the BAIM Drive folder.</DialogDescription>
          </DialogHeader>
          {viewInv && (
            <iframe
              src={`${api.defaults.baseURL}/invoices/${viewInv.invoice_id}/pdf?v=${pdfCacheBuster}#toolbar=0&navpanes=0`}
              title={viewInv.number}
              className="flex-1 w-full border-0 bg-zinc-100"
              data-testid="invoice-iframe"
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!payTarget} onOpenChange={(o) => !o && setPayTarget(null)}>
        <DialogContent data-testid="mark-paid-dialog" className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Mark {payTarget?.number} as paid</DialogTitle>
            <DialogDescription className="text-[11px] text-zinc-500">
              Détails du paiement — ces informations seront archivées sur la facture et apparaîtront dans le registre des factures payées (page Cabinet → Factures payées).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Date opération <span className="text-red-600">*</span></Label>
                <Input type="date" value={payForm.paid_date} onChange={(e) => setPayForm({ ...payForm, paid_date: e.target.value })} data-testid="mark-paid-date" />
              </div>
              <div>
                <Label>Montant payé ({payTarget?.currency}) <span className="text-red-600">*</span></Label>
                <Input type="number" step="0.01" value={payForm.paid_amount} onChange={(e) => setPayForm({ ...payForm, paid_amount: e.target.value })} data-testid="mark-paid-amount" />
              </div>
            </div>
            {payTarget?.currency && payTarget.currency !== "MAD" && (
              <div>
                <Label>Montant reçu en MAD <span className="text-[10px] text-zinc-400 font-normal">(contre-valeur créditée en banque — sinon le taux de change des Settings est appliqué)</span></Label>
                <Input type="number" step="0.01" value={payForm.paid_amount_mad} onChange={(e) => setPayForm({ ...payForm, paid_amount_mad: e.target.value })} data-testid="mark-paid-amount-mad" placeholder="ex : 90500.00" />
              </div>
            )}
            <div>
              <Label>Mode de paiement <span className="text-red-600">*</span></Label>
              <select
                value={payForm.payment_mode || "VIREMENT"}
                onChange={(e) => setPayForm({ ...payForm, payment_mode: e.target.value })}
                data-testid="mark-paid-mode"
                className="w-full h-10 px-3 border border-zinc-300 rounded-md text-sm"
              >
                <option value="VIREMENT">Virement</option>
                <option value="CHEQUE">Chèque</option>
                <option value="ESPECES">Espèces</option>
                <option value="CARTE">Carte</option>
                <option value="PRELEVEMENT">Prélèvement</option>
                <option value="AUTRE">Autre</option>
              </select>
            </div>
            <div>
              <Label>Référence (N° virement, chèque, SWIFT…) <span className="text-red-600">*</span></Label>
              <Input
                value={payForm.payment_reference || ""}
                onChange={(e) => setPayForm({ ...payForm, payment_reference: e.target.value })}
                placeholder="VIR-202605-001"
                data-testid="mark-paid-reference"
                maxLength={120}
              />
            </div>
            <div>
              <Label>Commentaire (optionnel)</Label>
              <textarea
                value={payForm.payment_comment || ""}
                onChange={(e) => setPayForm({ ...payForm, payment_comment: e.target.value })}
                placeholder="Ex: paiement partiel — solde le 15/06"
                data-testid="mark-paid-comment"
                maxLength={1000}
                rows={2}
                className="w-full px-3 py-2 border border-zinc-300 rounded-md text-sm resize-y"
              />
            </div>
            <div>
              <Label>Justificatif — PDF ou image (optionnel)</Label>
              <input
                type="file"
                accept=".pdf,.png,.jpg,.jpeg,.webp,application/pdf,image/png,image/jpeg,image/webp"
                onChange={(e) => setPayProofFile(e.target.files?.[0] || null)}
                data-testid="mark-paid-proof-input"
                className="w-full text-xs file:mr-3 file:px-3 file:h-8 file:rounded-md file:border-0 file:bg-zinc-100 file:text-zinc-700 file:text-xs hover:file:bg-zinc-200 file:cursor-pointer"
              />
              <p className="text-[10px] text-zinc-500 mt-1">
                Copie SWIFT, avis de virement… — archivé dans le dossier Drive « Swift » (Maintenance → Client invoices).
              </p>
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setPayTarget(null)} className="px-4 h-10 text-sm hover:bg-zinc-100 rounded-md">Cancel</button>
            <button onClick={submitMarkPaid} disabled={markingPaid} data-testid="mark-paid-confirm" className="inline-flex items-center gap-2 px-4 h-10 bg-emerald-600 text-white text-sm rounded-md hover:bg-emerald-700 disabled:opacity-50">
              <CheckCircle2 size={14} /> {markingPaid ? "Saving…" : "Confirm"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!sendTarget} onOpenChange={(o) => { if (!o) { setSendTarget(null); setSendError(null); setEmailPreview(null); setEditedBody(""); setEditedSubject(""); } }}>
        <DialogContent data-testid="send-invoice-dialog" className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Mail size={18} /> {sendTarget?.mode === "resend" ? "Renvoyer la facture" : "Send invoice"} {sendTarget?.inv?.number}</DialogTitle>
            <DialogDescription className="text-[11px] text-zinc-500 leading-relaxed">
              {sendTarget?.mode === "resend"
                ? "Vérifiez To, Cc, sujet, corps et pièces jointes avant de renvoyer. Le PDF original de la facture est ré-attaché tel quel (jamais régénéré) et le CRA est ajouté si la mission n'est pas en sous-traitance."
                : "Review the email below — you can edit the body text before sending. The PDF is attached, the signature is added at the bottom, and a read-receipt is requested."}
            </DialogDescription>
          </DialogHeader>
          {sendTarget && (
            <div className="space-y-3 py-2 text-xs">
              {previewLoading && (
                <div className="text-center py-4 text-zinc-500" data-testid="send-preview-loading">Loading preview…</div>
              )}
              {!previewLoading && emailPreview && (
                <>
                  <div className="grid grid-cols-[80px,1fr] gap-y-1.5 gap-x-3 bg-zinc-50 border border-zinc-200 rounded p-3">
                    <div className="text-zinc-500">From</div>
                    <div className="font-mono" data-testid="send-from-email">{emailPreview.from_alias}</div>
                    <div className="text-zinc-500">To</div>
                    <div data-testid="send-to-email">
                      {isCeo ? (
                        <>
                          <Input
                            data-testid="send-to-email-input"
                            type="email"
                            value={editedToEmail}
                            onChange={(e) => setEditedToEmail(e.target.value)}
                            className="h-8 text-xs font-mono"
                            placeholder="destinataire@client.com"
                          />
                          <div className="text-[10px] text-zinc-500 mt-0.5">
                            Destinataire par défaut (mission) : <span className="font-mono">{emailPreview.to_email}</span>
                          </div>
                        </>
                      ) : (
                        <div className="font-mono">{emailPreview.to_name ? `${emailPreview.to_name} <${emailPreview.to_email}>` : emailPreview.to_email}</div>
                      )}
                    </div>
                    <div className="text-zinc-500">Cc</div>
                    <div data-testid="send-cc-emails">
                      {isCeo ? (
                        <div className="space-y-1">
                          {editedCcList.length === 0 && (
                            <div className="text-[10px] text-zinc-400 italic font-mono">Aucun CC</div>
                          )}
                          {editedCcList.map((cc, idx) => (
                            <div key={idx} className="flex items-center gap-1">
                              <Input
                                data-testid={`send-cc-input-${idx}`}
                                type="email"
                                value={cc}
                                onChange={(e) => {
                                  const next = [...editedCcList];
                                  next[idx] = e.target.value;
                                  setEditedCcList(next);
                                }}
                                className="h-7 text-xs font-mono flex-1"
                                placeholder="cc@autre-domaine.com"
                              />
                              <button
                                type="button"
                                onClick={() => setEditedCcList(editedCcList.filter((_, i) => i !== idx))}
                                data-testid={`send-cc-remove-${idx}`}
                                className="w-6 h-7 flex items-center justify-center text-zinc-500 hover:text-red-600 hover:bg-red-50 rounded"
                                title="Retirer ce CC"
                              >
                                <X size={12} />
                              </button>
                            </div>
                          ))}
                          <button
                            type="button"
                            onClick={() => setEditedCcList([...editedCcList, ""])}
                            data-testid="send-cc-add"
                            className="inline-flex items-center gap-1 text-[11px] text-blue-700 hover:underline mt-1"
                          >
                            + Ajouter un CC
                          </button>
                        </div>
                      ) : (
                        <div className="font-mono text-[10px] text-zinc-700">{(emailPreview.cc_emails || []).join(", ") || "—"}</div>
                      )}
                    </div>
                    <div className="text-zinc-500">Subject</div>
                    <div data-testid="send-subject">
                      <Input
                        data-testid="send-subject-input"
                        value={editedSubject}
                        onChange={(e) => setEditedSubject(e.target.value)}
                        className="h-8 text-xs font-mono"
                      />
                    </div>
                    <div className="text-zinc-500">Attachment</div>
                    <div className="font-mono text-[10px] text-zinc-700" data-testid="send-attachments">
                      <div>{emailPreview.attachment_filename}</div>
                      {emailPreview.cra_attachment_filename && (
                        <div className="text-zinc-600">{emailPreview.cra_attachment_filename}</div>
                      )}
                      {/* Iter-229 — forfait milestone deliverable + other docs. */}
                      {(emailPreview.extra_attachment_filenames || []).map((n, ni) => (
                        <div key={ni} className="text-blue-800" data-testid={`send-extra-attachment-${ni}`}>{n}</div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <Label className="text-xs">Email body <span className="text-zinc-500 font-normal">(editable)</span></Label>
                      <button
                        type="button"
                        onClick={() => setEditedBody(emailPreview.body_text || "")}
                        data-testid="send-reset-body"
                        className="text-[10px] text-zinc-500 hover:text-zinc-800 font-mono uppercase"
                      >
                        Reset to template
                      </button>
                    </div>
                    <Textarea
                      data-testid="send-body-textarea"
                      value={editedBody}
                      onChange={(e) => setEditedBody(e.target.value)}
                      className="font-mono text-[11px] leading-relaxed min-h-[260px] resize-y"
                      spellCheck={false}
                    />
                    <div className="text-[10px] text-zinc-500 mt-1.5 flex items-center gap-1.5">
                      <Mail size={10} /> Signature image (BAIM logo) is appended automatically below the body.
                    </div>
                    {emailPreview.payment_banner_will_append && emailPreview.payment_declaration_url && (
                      <div className="mt-2 space-y-2" data-testid="send-payment-banner-notice">
                        <div className="flex items-center justify-between">
                          <Label className="text-xs flex items-center gap-1.5">
                            <span aria-hidden>💳</span>
                            Payment declaration banner
                            <span className="text-zinc-500 font-normal">(auto-appended below the body)</span>
                          </Label>
                          <a
                            href={emailPreview.payment_declaration_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[10px] text-zinc-500 hover:text-zinc-800 font-mono uppercase"
                            data-testid="send-payment-banner-url"
                          >
                            Test the link ↗
                          </a>
                        </div>
                        <div className="rounded-md border border-zinc-200 bg-zinc-50 p-2">
                          <div
                            data-testid="send-payment-banner-preview"
                            className="bg-white rounded border border-zinc-100 max-h-[260px] overflow-auto"
                            // Render the exact HTML snippet that will be sent — single
                            // source of truth from /lib/payment_cta_banner.py.
                            dangerouslySetInnerHTML={{ __html: emailPreview.payment_banner_html || "" }}
                          />
                        </div>
                        <div className="text-[10px] text-zinc-500 leading-relaxed">
                          This block is inserted automatically between your email body and the
                          BAIM signature. The recipient clicks "Declare the payment" to record
                          a payment in one click. The link is unique per invoice and valid for
                          90 days.
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}
              {sendError && (
                <div className="flex flex-col gap-2 bg-red-50 border border-red-200 rounded p-3" data-testid={`send-error-${sendError.code}`}>
                  <div className="flex gap-2 items-start">
                    <AlertCircle size={14} className="text-red-700 mt-0.5 flex-shrink-0" />
                    <div className="text-[11px] text-red-800 leading-relaxed">
                      <div className="font-semibold mb-0.5">Email NOT sent</div>
                      <div>{sendError.message || sendError.code}</div>
                      {sendError.code === "missing_mission_contact" && Array.isArray(sendError.missing) && (
                        <div className="mt-1.5 text-red-700">Missing fields: <span className="font-mono">{sendError.missing.join(", ")}</span></div>
                      )}
                    </div>
                  </div>
                  {sendError.code === "missing_mission_contact" && sendTarget?.mission && (
                    <div className="border-t border-red-200 pt-3 mt-1 space-y-2.5" data-testid="send-inline-contact-form">
                      <div className="text-[11px] text-red-900 font-semibold flex items-center gap-1.5">
                        <Mail size={12} /> Add the contact now — saved to the mission AND propagated to the client
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-[90px_1fr_1fr] gap-2">
                        <div>
                          <Label className="text-[10px] text-red-900">Civility</Label>
                          <select
                            data-testid="send-inline-contact-gender"
                            value={inlineContact.gender}
                            onChange={(e) => {
                              const g = e.target.value;
                              setInlineContact({ ...inlineContact, gender: g, ...(g === "M./Mme" ? { name: "" } : {}) });
                            }}
                            className="w-full h-10 border border-red-200 rounded-md px-2 text-xs bg-white"
                          >
                            <option value="">—</option>
                            <option value="M.">M.</option>
                            <option value="Mme.">Mme.</option>
                            <option value="M./Mme">M./Mme</option>
                          </select>
                        </div>
                        <div>
                          <Label className="text-[10px] text-red-900">Contact name</Label>
                          <Input
                            data-testid="send-inline-contact-name"
                            placeholder={inlineContact.gender === "M./Mme" ? "Unknown — not used in emails" : "Mr. Jean Dupont"}
                            value={inlineContact.name}
                            disabled={inlineContact.gender === "M./Mme"}
                            onChange={(e) => setInlineContact({ ...inlineContact, name: e.target.value })}
                            className={"text-xs" + (inlineContact.gender === "M./Mme" ? " bg-zinc-100 text-zinc-400" : "")}
                          />
                        </div>
                        <div>
                          <Label className="text-[10px] text-red-900">Contact email</Label>
                          <Input
                            data-testid="send-inline-contact-email"
                            type="email"
                            placeholder="contact@client.com"
                            value={inlineContact.email}
                            onChange={(e) => setInlineContact({ ...inlineContact, email: e.target.value })}
                            className="text-xs"
                          />
                        </div>
                      </div>
                      <div className="flex justify-end">
                        <button
                          onClick={saveInlineContact}
                          disabled={savingContact || !inlineContact.gender || !inlineContact.email.trim() || (inlineContact.gender !== "M./Mme" && !inlineContact.name.trim())}
                          data-testid="send-inline-contact-save"
                          className="inline-flex items-center gap-2 px-3 h-9 bg-red-700 text-white text-xs rounded-md hover:bg-red-800 disabled:opacity-50"
                        >
                          {savingContact ? "Saving…" : "Save contact and reload preview"}
                        </button>
                      </div>
                      <div className="text-[10px] text-red-700/80 italic leading-relaxed">
                        The contact will also be saved on the client {sendTarget.mission?.client_id ? <span className="font-mono">({sendTarget.mission.client_id})</span> : null} so other missions for the same client benefit from it next time.
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <button onClick={() => { setSendTarget(null); setSendError(null); setEmailPreview(null); setEditedBody(""); }} data-testid="send-cancel" className="px-4 h-10 text-sm hover:bg-zinc-100 rounded-md">Cancel</button>
            <button
              onClick={submitSend}
              disabled={sending || previewLoading || !emailPreview || !editedBody.trim()}
              data-testid="send-invoice-confirm"
              className="inline-flex items-center gap-2 px-4 h-10 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              <Mail size={14} /> {sending
                ? (sendTarget?.mode === "resend" ? "Renvoi…" : "Sending…")
                : (sendTarget?.mode === "resend" ? "Renvoyer l'email" : "Send email")}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      <Dialog open={!!cancelTarget} onOpenChange={(o) => { if (!o) { setCancelTarget(null); setCancelReason(""); } }}>
        <DialogContent data-testid="cancel-invoice-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700"><Ban size={18} /> Cancel invoice {cancelTarget?.number}</DialogTitle>
            <DialogDescription className="text-[11px] text-zinc-500 leading-relaxed">
              The invoice number <strong>{cancelTarget?.number}</strong> will be reserved and the PDF will be regenerated with a large <strong>“ANNULÉE”</strong> stamp + your reason. The number will <strong>never</strong> be re-used. This action cannot be reversed.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label>Cancellation reason <span className="text-red-600">*</span></Label>
              <Input
                data-testid="cancel-reason-input"
                placeholder="e.g. Data-entry mistake, double-billed by accident…"
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                autoFocus
              />
            </div>
            <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded p-2.5 leading-relaxed">
              The reason will be printed on the cancelled PDF below the “ANNULÉE” stamp for legal traceability.
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => { setCancelTarget(null); setCancelReason(""); }} className="px-4 h-10 text-sm hover:bg-zinc-100 rounded-md">Keep invoice</button>
            <button
              onClick={submitCancel}
              disabled={cancelling || !cancelReason.trim()}
              data-testid="cancel-invoice-confirm"
              className="inline-flex items-center gap-2 px-4 h-10 bg-red-700 text-white text-sm rounded-md hover:bg-red-800 disabled:opacity-50"
            >
              <Ban size={14} /> {cancelling ? "Cancelling…" : "Confirm cancellation"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter-240 — post-payment MAD counter-value dialog */}
      <Dialog open={!!madEditTarget} onOpenChange={(o) => !o && setMadEditTarget(null)}>
        <DialogContent data-testid="edit-mad-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle>Contre-valeur MAD — {madEditTarget?.number}</DialogTitle>
            <DialogDescription>
              Facture en {madEditTarget?.currency} ({Number(madEditTarget?.total_ttc || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2 })} {madEditTarget?.currency}).
              Le montant saisi est utilisé dans l'État mensuel et la Marge réelle. Laissez vide pour appliquer le taux de change des Settings.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Label>Montant reçu en MAD</Label>
            <Input
              type="number" step="0.01" value={madEditVal}
              onChange={(e) => setMadEditVal(e.target.value)}
              data-testid="edit-mad-input" placeholder="ex : 90500.00"
            />
          </div>
          <DialogFooter>
            <button onClick={() => setMadEditTarget(null)} className="px-3 h-9 border border-zinc-200 text-sm rounded-md hover:bg-zinc-50">Annuler</button>
            <button
              onClick={submitMadEdit} disabled={madSaving}
              data-testid="edit-mad-save"
              className="inline-flex items-center gap-1.5 px-4 h-9 bg-zinc-950 text-white text-sm rounded-md hover:bg-zinc-800 disabled:opacity-50"
            >
              {madSaving ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} />} Enregistrer
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter-261 — retro invoice edit history */}
      <Dialog open={!!retroHistoryTarget} onOpenChange={(o) => !o && setRetroHistoryTarget(null)}>
        <DialogContent data-testid="retro-history-dialog" className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Historique des corrections — {retroHistoryTarget?.number}</DialogTitle>
            <DialogDescription>Modifications apportées à cette facture rétroactive.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {[...(retroHistoryTarget?.retro_edit_history || [])].reverse().map((h, hi) => (
              <div key={hi} className="border border-zinc-200 rounded-md p-3" data-testid={`retro-history-entry-${hi}`}>
                <div className="flex items-center justify-between gap-2 text-xs text-zinc-500 mb-2">
                  <span className="font-medium text-zinc-800">{h.by_name || h.by}</span>
                  <span className="font-mono">{h.at ? new Date(h.at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }) : ""}</span>
                </div>
                <div className="space-y-1">
                  {(h.changes || []).map((c, ci) => (
                    <div key={ci} className="text-xs flex flex-wrap items-baseline gap-1.5">
                      <span className="font-medium text-zinc-700">{c.field} :</span>
                      <span className="line-through text-red-600/80 font-mono">{c.old ?? "—"}</span>
                      <span className="text-zinc-400">→</span>
                      <span className="text-emerald-700 font-mono">{c.new ?? "—"}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Iter-194v — Manual reminder dialog (auto-pick milestone vs overdue template) */}
      <ManualReminderDialog
        invoice={manualReminderInvoice}
        open={!!manualReminderInvoice}
        onClose={() => setManualReminderInvoice(null)}
        onSent={() => { setManualReminderInvoice(null); load(); }}
      />
    </div>
  );
}
